import { useRef } from "react";

export type BrowseTabId = "feed" | "boxes" | "products" | "producers" | "saved" | "following";

export const BROWSE_TABS: { id: BrowseTabId; label: string }[] = [
  { id: "feed", label: "Feed" },
  { id: "boxes", label: "Boxes" },
  { id: "products", label: "Listings" },
  { id: "producers", label: "Growers & Makers" },
  { id: "saved", label: "Saved" },
  { id: "following", label: "Following" },
];

// The feed is the landing page, not a peer of the browse modes, so it gets no
// pill — a sixth pill overflowed the width and clipped "Following". It stays
// the first swipe panel, so swiping right from Boxes still returns to it.
const PILL_TABS = BROWSE_TABS.filter((t) => t.id !== "feed");

interface BrowseModeNavProps {
  activeIndex: number;
  onSelect: (index: number) => void;
}

export function BrowseModeNav({ activeIndex, onSelect }: BrowseModeNavProps) {
  const tabsRef = useRef<(HTMLButtonElement | null)[]>([]);

  const panelIndexOf = (pillIndex: number) =>
    BROWSE_TABS.findIndex((t) => t.id === PILL_TABS[pillIndex].id);

  const focusTab = (pillIndex: number) => {
    const wrapped = (pillIndex + PILL_TABS.length) % PILL_TABS.length;
    onSelect(panelIndexOf(wrapped));
    tabsRef.current[wrapped]?.focus();
  };

  // With the feed showing, no pill is selected; keep the first one reachable.
  const activePillIndex = PILL_TABS.findIndex(
    (t) => t.id === BROWSE_TABS[activeIndex]?.id,
  );

  return (
    <div className="bg-card border-b">
      <div className="container mx-auto px-4 py-1">
        <div
          role="tablist"
          aria-label="Browse"
          aria-orientation="horizontal"
          className="-mx-2 flex justify-center gap-0.5 overflow-x-auto pb-0.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          {PILL_TABS.map((tab, pillIndex) => {
            const active = activePillIndex === pillIndex;
            return (
              <button
                key={tab.id}
                ref={(node) => {
                  tabsRef.current[pillIndex] = node;
                }}
                type="button"
                role="tab"
                id={`swipe-tab-${tab.id}`}
                aria-selected={active}
                aria-controls={`swipe-panel-${tab.id}`}
                tabIndex={active || (activePillIndex === -1 && pillIndex === 0) ? 0 : -1}
                onClick={() => onSelect(panelIndexOf(pillIndex))}
                onKeyDown={(e) => {
                  if (e.key === "ArrowRight") {
                    e.preventDefault();
                    focusTab(pillIndex + 1);
                  } else if (e.key === "ArrowLeft") {
                    e.preventDefault();
                    focusTab(pillIndex - 1);
                  } else if (e.key === "Home") {
                    e.preventDefault();
                    focusTab(0);
                  } else if (e.key === "End") {
                    e.preventDefault();
                    focusTab(PILL_TABS.length - 1);
                  }
                }}
                className={`shrink-0 rounded-full px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap ${
                  active
                    ? "bg-primary text-primary-foreground"
                    : "border border-border text-foreground"
                }`}
                data-testid={`mode-pill-${tab.id}`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
